import NavResponsiveModal from '@/components/Navigation/NavResponsiveModal';
import { BATCH_TIMEFRAME_CONFIRMATION } from '@/constants/apiRoutes';
import { post } from '@/utilities/onwardClient';
import { FormControlLabel, Grid, Radio, RadioGroup } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { captureException } from '@sentry/react';
import { useApolloClient } from '@apollo/client';
import { SUBMIT_RECONCILE } from '../graphql/queries';
import {
    Body1,
    Body2,
    GridItemRow,
    ModalActions,
    ModalContent,
    ModalTitle,
    PrimaryButton,
    SecondaryButton,
} from '../blocks';
import { MODALS } from '../constants';
import { PlanningContext } from '../context';
import { useAfterHours } from '../hooks';
import { useClientUser } from '@/hooks';
import { analyzeBatchSelection } from '../util/batchTimeframeConfirmation';

const BatchTimeframeConfirmationModal = () => {
    const {
        modalOpen,
        setModalOpen,
        selectedOrders,
        setSelectedOrders,
        setError,
        setNotification,
        state: { orders, routes },
        callbacks: { refetch },
    } = useContext(PlanningContext);
    const { circles } = useClientUser();
    const client = useApolloClient();

    const [confirmationType, setConfirmationType] = useState('shipperPreference');
    const [loading, setLoading] = useState(false);

    const open = modalOpen === MODALS.BATCH_TIMEFRAME_CONFIRMATION;

    const hasAutoconfirmAllStops = circles?.['autoconfirm-all-stops'] === true;

    // When the shipper auto-confirms everything, lock the method to autoConfirm.
    useEffect(() => {
        if (open && hasAutoconfirmAllStops) {
            setConfirmationType('autoConfirm');
        }
    }, [open, hasAutoconfirmAllStops]);

    const selectedOrderObjs = useMemo(
        () => (orders || []).filter((o) => selectedOrders[`${o.order_id}_${o.crossdock_leg}`]),
        [orders, selectedOrders]
    );

    const { assignedOrderIds, unassignedOrderNumbers, willAlsoConfirmOrderNumbers } = useMemo(
        () => analyzeBatchSelection(selectedOrderObjs, routes),
        [selectedOrderObjs, routes]
    );

    // Reconcile targets for the submit endpoint (which returns counts only,
    // no entity payload): every route an assigned selection sits on, and
    // every order the server will touch — assigned orders plus stopmates on
    // a shared stop (over-inclusive is safe; this just widens the refetch).
    const { affectedRouteIds, affectedOrderIds } = useMemo(() => {
        const routeIds = new Set();
        const orderIds = new Set(assignedOrderIds);
        const routesById = Object.fromEntries((routes || []).map((r) => [r.route_id, r]));
        for (const o of selectedOrderObjs) {
            const routeId = o.crossdock_leg === 'pickup' ? o.pickup_route_id : o.dropoff_route_id;
            if (!routeId) continue;
            routeIds.add(routeId);
            const route = routesById[routeId];
            for (const stop of route?.stopsByRouteId || []) {
                if ((stop.orders || []).includes(o.order_id)) {
                    stop.orders.forEach((id) => orderIds.add(id));
                }
            }
        }
        return { affectedRouteIds: [...routeIds], affectedOrderIds: [...orderIds] };
    }, [selectedOrderObjs, routes, assignedOrderIds]);

    // Representative route for the after-hours timezone check.
    const representativeRoute = useMemo(() => {
        const first = selectedOrderObjs.find((o) =>
            o.crossdock_leg === 'pickup' ? o.pickup_route_id : o.dropoff_route_id
        );
        const routeId = first?.crossdock_leg === 'pickup' ? first?.pickup_route_id : first?.dropoff_route_id;
        return (routes || []).find((r) => r.route_id === routeId);
    }, [selectedOrderObjs, routes]);

    const afterHours = useAfterHours(representativeRoute);

    // Customer (delivery / return) orders eligible to send that have no phone.
    const missingPhoneNumbers = useMemo(() => {
        const assignedIdSet = new Set(assignedOrderIds);
        return selectedOrderObjs
            .filter((o) => assignedIdSet.has(o.order_id) && !o.dropoff_phone)
            .map((o) => o.order_number);
    }, [selectedOrderObjs, assignedOrderIds]);

    const handleModalClose = () => {
        setLoading(false);
        setModalOpen(null);
        setConfirmationType(hasAutoconfirmAllStops ? 'autoConfirm' : 'shipperPreference');
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);

            const response = await post(BATCH_TIMEFRAME_CONFIRMATION, {
                orderIds: assignedOrderIds,
                sendSMS: confirmationType === 'sms',
                useRobocall: confirmationType === 'robocall',
                useShipperPreference: confirmationType === 'shipperPreference',
                autoConfirm: confirmationType === 'autoConfirm',
            });

            const data = response?.data || {};
            const errors = data.errors || [];
            const stopsProcessed = data.stopsProcessed || 0;

            if (response.status !== 200) {
                throw new Error(data.error || 'Unexpected response');
            }

            if (errors.length > 0) {
                // Some confirmations went out, some failed — surface the failures.
                captureException(new Error(`Batch timeframe confirmation partial failure: ${errors.join('; ')}`));
                setNotification({
                    severity: 'warning',
                    message: `Sent ${stopsProcessed} confirmation${
                        stopsProcessed === 1 ? '' : 's'
                    }; some could not be sent: ${errors.join('; ')}`,
                });
            } else if (stopsProcessed === 0) {
                // Nothing was actually sent (e.g. selected stops were already confirmed or completed).
                setNotification({
                    severity: 'info',
                    message:
                        'No timeframe confirmations were sent — the selected stops may already be confirmed or completed.',
                });
            } else {
                setNotification({
                    severity: 'success',
                    message: `Sent timeframe confirmations for ${stopsProcessed} stop${
                        stopsProcessed === 1 ? '' : 's'
                    }.`,
                });
            }

            client
                .query({
                    query: SUBMIT_RECONCILE,
                    variables: { route_ids: affectedRouteIds, order_ids: affectedOrderIds },
                    fetchPolicy: 'network-only',
                })
                .catch(() => refetch());
            setSelectedOrders({});
            handleModalClose();
        } catch (error) {
            setError(error, 'Error sending timeframe confirmations, please try again');
            setLoading(false);
        }
    };

    if (!open) return null;

    const nothingToSend = assignedOrderIds.length === 0;

    return (
        <NavResponsiveModal open={open} onClose={handleModalClose}>
            <ModalTitle>Send Timeframe Confirmations</ModalTitle>
            <ModalContent>
                {nothingToSend ? (
                    <GridItemRow>
                        <Alert severity="info">
                            None of the selected orders are assigned to a route yet. Confirmations can only be sent for
                            routed orders.
                        </Alert>
                    </GridItemRow>
                ) : (
                    <GridItemRow>
                        <Body2>
                            Sending confirmations for {assignedOrderIds.length} order
                            {assignedOrderIds.length === 1 ? '' : 's'}.
                        </Body2>
                    </GridItemRow>
                )}

                {unassignedOrderNumbers.length > 0 && (
                    <GridItemRow>
                        <Alert severity="warning">
                            The following selected orders are not assigned to a route and will be skipped:{' '}
                            {unassignedOrderNumbers.join(', ')}
                        </Alert>
                    </GridItemRow>
                )}

                {willAlsoConfirmOrderNumbers.length > 0 && (
                    <GridItemRow>
                        <Alert severity="warning">
                            Some selected orders share a stop with orders you didn't select. Those orders will be
                            confirmed as well: {willAlsoConfirmOrderNumbers.join(', ')}
                        </Alert>
                    </GridItemRow>
                )}

                {afterHours && (confirmationType === 'sms' || confirmationType === 'robocall') && (
                    <GridItemRow>
                        <Alert severity="error">
                            {`You are about to ${
                                confirmationType === 'sms' ? 'send SMS messages to' : 'robocall'
                            } customers outside of regular business hours (8 AM - 5PM)`}
                        </Alert>
                    </GridItemRow>
                )}

                <GridItemRow>
                    <Alert severity="warning">
                        Verify all service times are correct. Customers only receive reschedule texts if timeframes
                        change by more than 30 minutes.
                    </Alert>
                </GridItemRow>

                <GridItemRow
                    css={css`
                        margin-top: 1.5rem !important;
                    `}
                >
                    <Body2>Customer communication options:</Body2>
                </GridItemRow>
                <RadioGroup
                    value={confirmationType}
                    onChange={(e) => setConfirmationType(e.target.value)}
                    css={css`
                        display: flex;
                        flex-direction: column;
                        gap: 1rem;
                    `}
                >
                    <GridItemRow>
                        <FormControlLabel
                            control={<Radio color="primary" />}
                            value={'shipperPreference'}
                            disabled={hasAutoconfirmAllStops}
                        />
                        <Grid container>
                            <Body1>Use each shipper's preferred confirmation method</Body1>
                            <Body2>
                                Each stop is confirmed according to its shipper's account setting. Orders whose shipper
                                hasn't set a preference will default to text & email.
                            </Body2>
                        </Grid>
                    </GridItemRow>
                    <GridItemRow>
                        <FormControlLabel
                            control={<Radio color="primary" />}
                            value={'sms'}
                            disabled={hasAutoconfirmAllStops}
                        />
                        <Grid container>
                            <Body1>Text and email customers to confirm timeframes</Body1>
                            <Body2>
                                Onward will use automated messages to relay timeframes and receive confirmations.
                            </Body2>
                        </Grid>
                    </GridItemRow>
                    <GridItemRow>
                        <FormControlLabel
                            control={<Radio color="primary" />}
                            value={'robocall'}
                            disabled={hasAutoconfirmAllStops}
                        />
                        <Grid container>
                            <Body1>Robocall customers to confirm timeframes</Body1>
                            <Body2>
                                Onward will place an automated phone call to relay timeframes and capture confirmations
                                via keypad response.
                            </Body2>
                        </Grid>
                    </GridItemRow>
                    <GridItemRow>
                        <FormControlLabel control={<Radio color="primary" />} value={'autoConfirm'} />
                        <Grid container>
                            <Body1>Autoconfirm all timeframes</Body1>
                            <Body2>No texts or emails will be sent. All stops will be automatically confirmed.</Body2>
                        </Grid>
                    </GridItemRow>
                </RadioGroup>

                {missingPhoneNumbers.length > 0 && (confirmationType === 'sms' || confirmationType === 'robocall') && (
                    <Body2
                        style={{ color: 'red' }}
                        css={css`
                            margin-top: 1rem;
                        `}
                    >
                        Warning: The following orders are missing a customer phone number:{' '}
                        {missingPhoneNumbers.join(', ')}
                    </Body2>
                )}
            </ModalContent>
            <ModalActions>
                <SecondaryButton disabled={loading} onClick={handleModalClose}>
                    Cancel
                </SecondaryButton>
                <PrimaryButton disabled={loading || nothingToSend} onClick={handleSubmit}>
                    Send Confirmations
                </PrimaryButton>
            </ModalActions>
        </NavResponsiveModal>
    );
};

export default BatchTimeframeConfirmationModal;
